/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { MutableRefObject, useState } from 'react';
import styled from 'styled-components';

import { Modal, Button, LoadingButton } from '@common/index';
import { Alert, Stack, Switch, Chip } from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { flowActions, tabActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { CodeOutlined, PlayArrowOutlined } from '@mui/icons-material';
import { Label } from '@common/Label';
import { BasicInput } from '@common/Expression/Basic';
import { Translation } from '@components/Flow';
import { NodeRequestParameter } from '@components/Flow/Nodes';
import { isNodeError, runWorkflow } from '@store/async/run';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useTranslate } from '@common/hooks/useTranslate';
import { getRequestParameters, getWorkflowParameters } from '@common/utils/execution';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  gap: 1.5rem;
  padding-bottom: 0.5rem;
`;

const NodeContainer = styled.div`
  #type {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 13px;
    margin: 0;
  }
`;

type VariableMap = { [key: string]: NodeRequestParameter<string> };

interface NodeProps {
  staticVariable: NodeRequestParameter<string>;
  variables: VariableMap;
  setVariables: React.Dispatch<React.SetStateAction<VariableMap>>;
}

const NodeTestRunVariable: React.FC<NodeProps> = ({ staticVariable, variables, setVariables }) => {
  const dispatch = useDispatch();
  const savedParameters = useSelector((state: State) => state.tabs.active.run.savedParameters);

  const { id, name } = staticVariable;
  const isValueSaved = Boolean(savedParameters[id]);
  const _variable = isValueSaved ? savedParameters[id] : variables[id];
  const varValue = _variable?.value || '';

  const changeSavedValue = (state: boolean) => {
    if (state) {
      dispatch(tabActions.addSavedParameter({ id, value: _variable }));
    } else {
      if (!_variable) setVariables((prev) => ({ ...prev, [id]: savedParameters[id] }));
      dispatch(tabActions.deleteSavedParameter(id));
    }
  };

  return (
    <NodeContainer>
      <Stack spacing={1}>
        <Stack display='flex' justifyContent='space-between' direction='row'>
          <Stack direction='row' spacing={0.75} alignItems='center'>
            <Label
              title={name}
              tooltip='This dynamic variable was used in an input field for this node. You need to provide a value for this variable to execute the workflow.'
            />
            <Chip
              disabled
              skipFocusWhenDisabled
              label={staticVariable?.type || 'unknown'}
              variant='outlined'
              size='small'
              sx={{
                fontSize: '9px',
                height: 'fit-content',
                paddingTop: '1px',
                paddingBottom: '1.5px',
              }}
            />
          </Stack>
          <Stack direction='row' spacing={0.75} alignItems='center'>
            <p id='type'>Save Value</p>
            <Switch
              size='small'
              disabled={!name}
              checked={isValueSaved}
              onChange={(e) => changeSavedValue(e.target.checked)}
            />
          </Stack>
        </Stack>
        <BasicInput
          key={id}
          disabled={isValueSaved}
          keyProp={id}
          value={varValue}
          setValue={(newValue: string) =>
            setVariables((prev) => ({
              ...prev,
              [id]: {
                ...(prev[id] || staticVariable),
                value: newValue,
              },
            }))
          }
          placeholder='Value'
          minRows={1}
          maxRows={12}
        />
      </Stack>
    </NodeContainer>
  );
};

interface Props {
  abortControllerRef: MutableRefObject<AbortController | null>;
  reset: () => void;
  start: () => void;
  pause: () => void;
}

export const WorkflowRunParameters: React.FC<Props> = ({ abortControllerRef, start, pause, reset }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [variables, setVariables] = useState<VariableMap>({});

  const { service_id, workflow_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useTranslate();

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const testRun = useSelector((state: State) => state.flow.workflowTestRun);
  const savedParameters = useSelector((state: State) => state.tabs.active.run.savedParameters);
  const setOpen = (val: Translation | null) => dispatch(flowActions.setWorkflowTestRun(val));

  const trigger = commands.find((command) => command.config.trigger);
  const isModule = trigger?.config.command_id === 'workflow.module';
  const workflowType = isModule ? 'Module' : 'Workflow';

  const completionNodes = commands.filter((command) => command.config.command_id === 'llm.completion');
  const isMessageArray = completionNodes.find(
    (node) => node.state?.message_array?.enabled && node?.state?.messages?.type !== 'message_expression'
  );

  const triggerNode = testRun?.nodes.find((node) => node.trigger);
  const allParameters = getWorkflowParameters(triggerNode);

  const closeModal = () => {
    setVariables({});
    setOpen(null);
  };

  const areAllVariablesFilled = allParameters.every(
    (param) => savedParameters[param.id]?.name || (param?.name && param?.name.length > 0)
  );

  const executeWorkflow = async () => {
    reset();

    const workspace_id = service_id;
    const translation = translate(workflow_id || '');

    start();
    setOpen(null);
    setVariables({});

    abortControllerRef.current = new AbortController();

    /**
     * @TODO The method for getting module parameters and request parameters are very similiar but slightly different
     * We should make a standard for passing any type of parameter from the trigger node to the backend for execution
     */

    const parameters = getRequestParameters(triggerNode, allParameters, variables, savedParameters);
    const response = await dispatch(
      runWorkflow({
        workspace_id,
        workflow_id,
        parameters,
        translation,
        abortController: abortControllerRef.current,
      })
    );

    pause();

    if (isNodeError(response.payload)) {
      enqueueSnackbar(response.payload.error.execution.reason, { variant: 'error', autoHideDuration: 8000 });
      enqueueSnackbar(`${workflowType} Execution Failed`, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  return (
    <Modal
      open={Boolean(testRun)}
      background='black'
      setOpen={closeModal}
      header={`Run ${workflowType}`}
      info={`This ${workflowType.toLowerCase()} has dynamic parameters that need to given a value before it can be executed.`}
      icon={<CodeOutlined />}
      body={
        <Container>
          {isMessageArray && (
            <Alert severity='warning'>
              {`The run ${workflowType.toLowerCase()} feature is unsupported when the message array is defined as an expression. If you would like to run this ${workflowType.toLowerCase()}, open the sidebar of the completion node (${
                isMessageArray?.node.id
              }) and change the "Messages" input box to "Fixed" by hovering.`}
            </Alert>
          )}
          {!isMessageArray &&
            allParameters.map((variable) => (
              <NodeTestRunVariable
                key={variable.id}
                staticVariable={variable}
                variables={variables}
                setVariables={setVariables}
              />
            ))}
        </Container>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={closeModal}>
            Close
          </Button>
          <LoadingButton
            onClick={executeWorkflow}
            // loading={loading}
            minWidth='100px'
            small
            customColor='primary'
            startIcon={<PlayArrowOutlined />}
            disabled={Boolean(!areAllVariablesFilled || isMessageArray)}
          >
            Execute
          </LoadingButton>
        </Stack>
      }
    />
  );
};
